import { useNavigate, Link } from "react-router-dom";
import { SuitePromo } from "../components/ProfimySuite";
import { useState, useEffect, useRef } from "react";
import {
  Zap, TrendingUp, Bell, Search, Eye, Download, Shield, Check,
  ArrowRight, Crown, ChevronRight, ArrowDown, ArrowUp, Clock,
  Brain, Rocket, Target, BarChart3, Star, Trophy, Timer,
  Crosshair, Layers, Sparkles, ExternalLink, X, Mail,
  ShoppingCart, Package, Truck, Users,
} from "lucide-react";
import { Input } from "../components/ui/input";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Get current time in France (Europe/Paris)
function getNowInFrance() {
  const str = new Date().toLocaleString("en-US", { timeZone: "Europe/Paris" });
  return new Date(str);
}

// Countdown: 8 Avril 2026 à 23h59:59 heure française
const COUNTDOWN_END = (() => {
  // Create target date in French timezone: April 8, 2026 23:59:59
  const target = new Date("2026-04-08T23:59:59+02:00");
  return target.getTime();
})();

function useCountdown() {
  const [timeLeft, setTimeLeft] = useState(getTimeLeft());
  function getTimeLeft() {
    const now = getNowInFrance();
    const diff = COUNTDOWN_END - now.getTime();
    if (diff <= 0) return null;
    return {
      days: Math.floor(diff / 86400000),
      hours: Math.floor((diff % 86400000) / 3600000),
      minutes: Math.floor((diff % 3600000) / 60000),
      seconds: Math.floor((diff % 60000) / 1000),
    };
  }
  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(getTimeLeft()), 1000);
    return () => clearInterval(timer);
  }, []);
  return timeLeft;
}

function CountdownBanner() {
  const tl = useCountdown();
  if (!tl) return null;
  return (
    <div className="flex items-center justify-center gap-2 text-sm" data-testid="countdown">
      <Clock size={14} className="text-[#FFCC00]" />
      <span className="text-white/60 font-bold uppercase tracking-wider text-xs">L'offre prend fin dans :</span>
      <div className="flex gap-1.5">
        {[
          { v: tl.days, l: "j" }, { v: tl.hours, l: "h" },
          { v: tl.minutes, l: "m" }, { v: tl.seconds, l: "s" },
        ].map(({ v, l }) => (
          <span key={l} className="bg-[#0A0A0A] border border-[#FFCC00]/40 text-[#FFCC00] font-extrabold text-xs px-2 py-1 min-w-[36px] text-center">
            {String(v).padStart(2, "0")}{l}
          </span>
        ))}
      </div>
    </div>
  );
}

// Newsletter Popup
function NewsletterPopup({ show, onClose }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const tl = useCountdown();

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      toast.success("Inscription réussie !");
      onClose();
    } catch {
      toast.error("Erreur, réessayez.");
    }
    setSubmitting(false);
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" data-testid="newsletter-popup">
      <div className="bg-[#0A0A0A] border-2 border-[#00E676] max-w-md w-full p-6 sm:p-8 relative">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/40 hover:text-white">
          <X size={20} />
        </button>
        <div className="text-center">
          <div className="inline-block bg-[#FFCC00]/10 border border-[#FFCC00]/40 text-[#FFCC00] px-4 py-1.5 font-bold text-xs uppercase tracking-[0.2em] mb-4">
            Offre limitée
          </div>
          <h3 className="text-xl sm:text-2xl font-extrabold text-white mb-2">
            Ne rate pas cette offre !
          </h3>
          <p className="text-white/50 text-sm mb-4">
            Inscris-toi pour être le premier informé des meilleures opportunités d'arbitrage.
          </p>
          {tl && (
            <div className="mb-5">
              <CountdownBanner />
            </div>
          )}
          <form onSubmit={handleSubmit} className="flex flex-col gap-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" size={18} />
              <Input
                type="email"
                placeholder="Ton email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-transparent border-2 border-white/20 text-white pl-10 h-12 focus:border-[#00E676]"
                data-testid="newsletter-email-input"
              />
            </div>
            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3 bg-[#00E676] text-[#050505] font-bold text-sm uppercase tracking-wider flex items-center justify-center gap-2 hover:shadow-[0_0_20px_rgba(0,230,118,0.4)] transition-all"
              data-testid="newsletter-submit-btn"
            >
              <Zap size={16} />
              {submitting ? "..." : "En savoir plus"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

const DEALS = [
  { product: "PlayStation 5", image: "https://images.unsplash.com/photo-1606813907291-d86efa9b94db?w=400&h=300&fit=crop", buyPrice: 300, sellPrice: 500, buyPlatform: "Leboncoin", sellPlatform: "eBay", profit: 200, profitPercent: 67 },
  { product: "Nike Air Jordan 1", image: "https://images.unsplash.com/photo-1645833889386-2782e290ee3b?w=400&h=300&fit=crop", buyPrice: 89, sellPrice: 210, buyPlatform: "Vinted", sellPlatform: "eBay", profit: 121, profitPercent: 136 },
  { product: "iPhone 15 Pro", image: "https://images.unsplash.com/photo-1697898734230-705d8fc551e9?w=400&h=300&fit=crop", buyPrice: 650, sellPrice: 950, buyPlatform: "Rakuten", sellPlatform: "Amazon", profit: 300, profitPercent: 46 },
];

const FEATURES = [
  { icon: Search, title: "Scanner Intelligent", desc: "Recherchez n'importe quel produit et comparez les prix entre toutes les marketplaces instantanément." },
  { icon: TrendingUp, title: "Opportunités d'Arbitrage", desc: "Détectez les écarts de prix entre plateformes et calculez votre marge nette automatiquement." },
  { icon: Bell, title: "Alertes Temps Réel", desc: "Recevez des notifications Telegram et Email dès qu'une opportunité haute marge apparaît." },
  { icon: Eye, title: "Watchlist & Suivi", desc: "Sauvegardez vos produits favoris et suivez l'évolution de leurs prix automatiquement." },
  { icon: Download, title: "Export CSV/Excel", desc: "Exportez toutes vos opportunités et votre watchlist en un clic pour analyse approfondie." },
  { icon: Shield, title: "Multi-marchés", desc: "Scannez les marchés France, USA, Allemagne et UK avec un simple clic." },
];

const MARKETPLACES = [
  "eBay", "Amazon", "Cdiscount", "Aliexpress", "Vinted", "Leboncoin",
  "Fnac", "Rakuten", "Etsy", "BackMarket", "Temu", "TikTok Shop",
];

const PLAN_FEATURES = [
  "Scanner illimité de produits",
  "Alertes Telegram & Email en temps réel",
  "Watchlist avec suivi automatique",
  "Export CSV/Excel illimité",
  "Accès à toutes les marketplaces",
  "Multi-marchés : FR, USA, DE, UK",
  "Mises à jour quotidiennes",
  "Support prioritaire",
];

const BENEFITS = [
  { icon: Timer, title: "Gain de temps massif", desc: "Fini les heures passées à comparer manuellement les prix. Un seul clic et c'est fait.", accent: "#00E676" },
  { icon: BarChart3, title: "Décisions basées sur la data", desc: "Ne te fie plus à ton instinct. Laisse les chiffres parler et prends des décisions rentables.", accent: "#00E676" },
  { icon: Crosshair, title: "Opportunités invisibles", desc: "Accède à des deals que personne d'autre ne voit. L'avantage de ceux qui ont le bon outil.", accent: "#FFCC00" },
  { icon: Zap, title: "Réactivité maximale", desc: "Fais ton cash avant la rupture de stock. Les bonnes affaires ne durent pas, toi non plus.", accent: "#00E676" },
  { icon: Rocket, title: "Scalabilité totale", desc: "Passe du side hustle à un vrai business. L'outil grandit avec ton ambition.", accent: "#FFCC00" },
  { icon: Brain, title: "Zéro charge mentale", desc: "Plus besoin de tout retenir. L'outil scanne, alerte et calcule pour toi.", accent: "#00E676" },
  { icon: Trophy, title: "Avantage concurrentiel énorme", desc: "Pendant que les autres cherchent, toi tu trouves. Devance la concurrence à chaque deal.", accent: "#FFCC00" },
  { icon: Layers, title: "Arbitrage multi-plateformes FACILE", desc: "Jongle entre les marketplaces en quelques clics. eBay, Amazon, Vinted, Leboncoin... tout centralisé.", accent: "#00E676" },
];

const TRUSTPILOT_REVIEWS = [
  { name: "Lydie", rating: 5, text: "Formation utile, claire, pratique, et à petit prix en plus. Service support très réactif. Je suis la formation ebay depuis décembre 2024 à mon rythme et je suis très satisfaite du déroulement. Franchement, je recommande Joseph.", date: "5 février 2025" },
  { name: "Pascal", rating: 5, text: "Joseph est, sans nul doute, l'un des meilleurs formateurs du moment dans le domaine des marketplaces. Il s'adapte très rapidement aux nouvelles technologies (l'IA) pour nous proposer des nouveautés vraiment topissimes.", date: "30 avril 2025" },
  { name: "Sebinoux", rating: 5, text: "J'adore Joseph et les formations sont au Top, en tant que malentendant, tout est possible !", date: "8 février 2025" },
  { name: "Client vérifié", rating: 5, text: "Joseph est une personne en or ! À l'écoute, sincère, toujours là pour donner un coup de main. Ses formations sont au top et à un prix plus qu'abordable !", date: "7 avril 2025", verified: true },
  { name: "Client", rating: 5, text: "J'avais acheté la formation AR Boost en 2024. Un an plus tard, mon business a littéralement changé de dimension. Grâce aux méthodes claires et efficaces de Joseph, j'ai largement rentabilisé la formation.", date: "15 avril 2025" },
  { name: "Client", rating: 5, text: "Croyez en ce qui sort de la bouche de Joseph, appliquez ses conseils, les résultats vont vous surprendre. En 28 jours la vente devient un jeu d'enfant.", date: "10 octobre 2025" },
];

const USE_CASES = [
  { icon: ShoppingCart, title: "Achat-Revente", desc: "Trouvez les produits au meilleur prix et revendez-les avec une marge maximale." },
  { icon: Package, title: "Dropshipping", desc: "Identifiez les fournisseurs les moins chers et vendez sans stock." },
  { icon: Truck, title: "Stock", desc: "Approvisionnez-vous intelligemment en comparant les prix fournisseurs." },
  { icon: Zap, title: "Flux Tendu", desc: "Réagissez en temps réel aux fluctuations de prix du marché." },
];

const LEVELS = [
  { title: "Débutants", desc: "Interface simple, suggestions automatiques, formations incluses.", color: "#00E676" },
  { title: "Intermédiaires", desc: "Scanner multi-modes, alertes personnalisées, export données.", color: "#FFCC00" },
  { title: "Avancés", desc: "Multi-marchés internationaux, 500 produits, 300+ fournisseurs, API.", color: "#FF6B35" },
];

function StarRating({ rating }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} size={16} className={i <= rating ? "text-[#00E676] fill-[#00E676]" : "text-white/20"} />
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const [showPopup, setShowPopup] = useState(false);
  const popupShown = useRef(false);

  // Popup after 9s + exit intent
  useEffect(() => {
    const timer = setTimeout(() => {
      if (!popupShown.current) { popupShown.current = true; setShowPopup(true); }
    }, 9000);

    const handleMouseLeave = (e) => {
      if (e.clientY <= 0 && !popupShown.current) {
        popupShown.current = true;
        setShowPopup(true);
      }
    };
    document.addEventListener("mouseleave", handleMouseLeave);
    return () => { clearTimeout(timer); document.removeEventListener("mouseleave", handleMouseLeave); };
  }, []);

  const handleCTA = () => navigate("/login");

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="landing-page">
      <NewsletterPopup show={showPopup} onClose={() => setShowPopup(false)} />

      {/* NAV */}
      <nav className="border-b border-[#00E676]/20 px-4 sm:px-6 py-3 sm:py-4">
        <div className="max-w-7xl mx-auto flex flex-wrap gap-3 items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="bg-[#00E676] text-[#050505] px-2 py-0.5 sm:px-3 sm:py-1 font-extrabold text-base sm:text-xl tracking-tight">PROFIMY</span>
            <span className="font-extrabold text-base sm:text-xl text-white">MARGIN</span>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/outils/profimy-app" className="text-xs sm:text-sm font-bold text-[#00E676] hover:underline">Nos outils</Link>
            <button onClick={() => navigate("/login")} className="hidden md:flex items-center gap-2 px-4 py-2 border border-[#00E676]/40 text-[#00E676] font-bold text-sm uppercase tracking-wider hover:bg-[#00E676]/10 transition-colors" data-testid="nav-login-btn">
              Connexion
            </button>
            <button onClick={handleCTA} className="flex items-center gap-1.5 sm:gap-2 px-3 sm:px-5 py-2 bg-[#00E676] text-[#050505] font-bold text-xs sm:text-sm uppercase tracking-wider hover:shadow-[0_0_20px_rgba(0,230,118,0.5)] transition-all" data-testid="nav-subscribe-btn">
              <Zap size={14} /> Commencer
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="relative px-4 sm:px-6 py-16 sm:py-24 md:py-32 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-[#00E676]/5 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-5xl mx-auto text-center relative z-10">
          <div className="inline-block bg-[#00E676]/10 border border-[#00E676]/40 text-[#00E676] px-5 py-2 font-bold text-sm uppercase tracking-[0.2em] mb-10">
            Sourcing Intelligent Multi-Plateformes
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-extrabold tracking-tight leading-[1.1] mb-6">
            Trouvez les meilleures
            <br />
            <span className="text-[#00E676] drop-shadow-[0_0_30px_rgba(0,230,118,0.4)]">opportunités d'arbitrage</span>
            <br />
            en temps réel
          </h1>
          <p className="text-lg md:text-xl text-white/60 max-w-2xl mx-auto mb-12">
            Comparez les prix entre +50 marketplaces, calculez vos marges automatiquement
            et recevez des alertes quand une bonne affaire apparaît.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <button onClick={handleCTA} className="group w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-[#00E676] text-[#050505] font-bold text-base sm:text-lg uppercase tracking-wider flex items-center justify-center gap-3 hover:shadow-[0_0_40px_rgba(0,230,118,0.4)] transition-all" data-testid="hero-cta-btn">
              <Zap size={20} />
              Commencer maintenant
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate("/login")} className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 border border-white/20 text-white/80 font-bold text-base sm:text-lg uppercase tracking-wider flex items-center justify-center gap-3 hover:border-[#00E676]/50 hover:text-[#00E676] transition-all" data-testid="hero-demo-btn">
              Voir le dashboard
            </button>
          </div>
          <div className="mt-5">
            <CountdownBanner />
          </div>
          <p className="text-sm text-white/30 mt-3 uppercase tracking-wider">
            Sans engagement — Annulable à tout moment
          </p>
        </div>
      </section>

      {/* ADAPTED FOR - Use Cases */}
      <section className="border-t border-[#00E676]/10 px-4 sm:px-6 py-12 sm:py-16" data-testid="use-cases-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <p className="text-xs uppercase tracking-[0.3em] text-[#00E676] font-bold mb-3">Adapté pour</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight">
              Profimy Margin s'adapte à <span className="text-[#00E676]">votre business</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {USE_CASES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-[#0A0A0A] border border-white/10 p-5 hover:border-[#00E676]/40 transition-all">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center flex-shrink-0">
                    <Icon size={20} className="text-[#00E676]" />
                  </div>
                  <h3 className="font-bold text-sm">{title}</h3>
                </div>
                <p className="text-xs text-white/40 leading-relaxed">{desc}</p>
                <div className="mt-3 flex items-center gap-1.5 text-[#00E676] text-xs font-bold">
                  <Check size={14} /> Compatible
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEVELS */}
      <section className="border-t border-[#00E676]/10 px-4 sm:px-6 py-12 sm:py-16" data-testid="levels-section">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-[#00E676] font-bold mb-3">Pour tous</p>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight mb-10">
            Adapté à <span className="text-[#00E676]">tous les niveaux</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {LEVELS.map(({ title, desc, color }) => (
              <div key={title} className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00E676]/40 transition-all text-center">
                <div className="w-14 h-14 mx-auto mb-4 border-2 flex items-center justify-center font-extrabold text-lg" style={{ borderColor: color, color }}>
                  {title === "Débutants" ? <Users size={24} /> : title === "Intermédiaires" ? <Target size={24} /> : <Rocket size={24} />}
                </div>
                <h3 className="font-bold text-lg mb-2" style={{ color }}>{title}</h3>
                <p className="text-sm text-white/40">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PAIN POINT */}
      <section className="relative px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10 overflow-hidden" data-testid="pain-point-section">
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FF3B30]/3 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <p className="text-[#FF3B30] font-bold uppercase tracking-[0.3em] text-sm mb-6">Le problème</p>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-8">
            Fini les heures de<br /><span className="text-[#FF3B30]">recherches inutiles</span> !!
          </h2>
          <p className="text-base sm:text-lg text-white/50 max-w-2xl mx-auto mb-10">
            Tu passes des heures à scroller Leboncoin, Vinted, eBay... à comparer les prix à la main...
            à calculer tes marges sur un bout de papier... pour au final rater les meilleures affaires.
          </p>
          <div className="inline-block border-2 border-[#00E676] p-8 relative">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#050505] px-4">
              <span className="text-[#00E676] font-bold uppercase tracking-wider text-sm">La solution</span>
            </div>
            <h3 className="text-2xl sm:text-3xl font-extrabold mb-3"><span className="text-[#00E676]">Profimy Margin</span> fait tout pour toi !</h3>
            <p className="text-white/60">Scanne. Compare. Calcule. Alerte. En un seul clic.</p>
          </div>
        </div>
      </section>

      {/* BIG PROMISE */}
      <section className="px-4 sm:px-6 py-12 sm:py-16 bg-[#00E676]" data-testid="big-promise-section">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight text-[#050505] leading-tight">GROSSE Marge FACILE en 1 clic !</h2>
          <p className="text-[#050505]/70 text-lg mt-3 font-bold">Repère les trous du marché et profite-en avant tout le monde.</p>
        </div>
      </section>

      {/* MARKETPLACES */}
      <section className="border-y border-[#00E676]/10 py-6 sm:py-8 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-center text-xs uppercase tracking-[0.3em] text-[#00E676]/60 font-bold mb-6">+50 Marketplaces supportées</p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {MARKETPLACES.map((mp) => (
              <span key={mp} className="border border-[#00E676]/20 text-white/70 px-4 py-2 text-sm font-bold uppercase tracking-wider hover:border-[#00E676]/50 hover:text-[#00E676] transition-colors cursor-default">{mp}</span>
            ))}
          </div>
        </div>
      </section>

      {/* SOCIAL PROOF */}
      <section className="px-4 sm:px-6 py-16 sm:py-20" data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#00E676] font-bold mb-3">Preuves en images</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Des deals trouvés par nos utilisateurs</h2>
            <p className="text-white/50 text-lg">Achetez moins cher ici, revendez plus cher là-bas. Simple.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {DEALS.map((deal, idx) => (
              <div key={idx} className="group relative bg-[#0A0A0A] border border-[#00E676]/20 overflow-hidden hover:border-[#00E676]/60 transition-all hover:shadow-[0_0_30px_rgba(0,230,118,0.1)]" data-testid={`deal-card-${idx}`}>
                <div className="h-48 overflow-hidden relative">
                  <img src={deal.image} alt={deal.product} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
                  <div className="absolute top-3 right-3 bg-[#00E676] text-[#050505] px-3 py-1 font-extrabold text-sm shadow-[0_0_15px_rgba(0,230,118,0.5)]">+{deal.profit}€ PROFIT</div>
                </div>
                <div className="p-6">
                  <h3 className="font-bold text-xl mb-4 text-white">{deal.product}</h3>
                  <div className="flex items-center justify-between mb-4">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-[#FF4444] mb-1"><ArrowDown size={14} /><span className="text-[10px] uppercase tracking-wider font-bold">Achat</span></div>
                      <p className="text-2xl font-extrabold text-white">{deal.buyPrice}€</p>
                      <p className="text-xs text-white/40 mt-1">{deal.buyPlatform}</p>
                    </div>
                    <div className="flex flex-col items-center px-4">
                      <ArrowRight className="text-[#00E676]" size={24} />
                      <span className="text-[10px] text-[#00E676] font-bold mt-1">+{deal.profitPercent}%</span>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-[#00E676] mb-1"><ArrowUp size={14} /><span className="text-[10px] uppercase tracking-wider font-bold">Vente</span></div>
                      <p className="text-2xl font-extrabold text-[#00E676] drop-shadow-[0_0_10px_rgba(0,230,118,0.3)]">{deal.sellPrice}€</p>
                      <p className="text-xs text-white/40 mt-1">{deal.sellPlatform}</p>
                    </div>
                  </div>
                  <div className="bg-[#00E676]/10 border border-[#00E676]/30 p-3 text-center">
                    <span className="text-[#00E676] font-extrabold text-lg">Marge nette : +{deal.profit}€</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-white/30 text-sm mt-8 uppercase tracking-wider">Trouvés grâce à Profimy Margin — Résultats réels de nos utilisateurs</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10 relative" data-testid="benefits-section">
        <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-[#00E676]/3 rounded-full blur-[150px] pointer-events-none" />
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-[#00E676] font-bold mb-3">Pourquoi Profimy Margin ?</p>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">8 raisons de commencer maintenant</h2>
            <p className="text-white/50 text-lg">Chaque jour sans Profimy Margin, c'est de l'argent que tu laisses sur la table.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            {BENEFITS.map(({ icon: Icon, title, desc, accent }, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00E676]/40 transition-all group">
                <div className="w-12 h-12 flex items-center justify-center mb-4 border" style={{ borderColor: `${accent}40`, backgroundColor: `${accent}10` }}>
                  <Icon size={22} style={{ color: accent }} />
                </div>
                <h3 className="font-bold text-base mb-2">{title}</h3>
                <p className="text-sm text-white/40 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ARBITRAGE */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10" data-testid="arbitrage-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block bg-[#FFCC00]/10 border border-[#FFCC00]/30 text-[#FFCC00] px-5 py-2 font-bold text-sm uppercase tracking-[0.2em] mb-8">Arbitrage Marketplaces</div>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-6">
            Jongle entre les marketplaces<br className="hidden sm:block" /> <span className="text-[#00E676]">en quelques clics</span>
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto mb-10">
            Repère les trous du marché et profite-en ! Un produit à 89€ sur Vinted peut se vendre 210€ sur eBay.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center"><Target className="text-[#00E676] mx-auto mb-3" size={32} /><h3 className="font-bold text-lg mb-1">Détecte</h3><p className="text-sm text-white/40">L'outil scanne toutes les plateformes et repère les écarts de prix</p></div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center"><BarChart3 className="text-[#FFCC00] mx-auto mb-3" size={32} /><h3 className="font-bold text-lg mb-1">Calcule</h3><p className="text-sm text-white/40">Marge nette calculée automatiquement, frais inclus</p></div>
            <div className="bg-[#0A0A0A] border border-white/10 p-6 text-center"><Zap className="text-[#00E676] mx-auto mb-3" size={32} /><h3 className="font-bold text-lg mb-1">Alerte</h3><p className="text-sm text-white/40">Tu reçois une notification avant tout le monde</p></div>
          </div>
          <button onClick={handleCTA} className="px-8 py-4 bg-[#00E676] text-[#050505] font-bold text-lg uppercase tracking-wider inline-flex items-center gap-3 hover:shadow-[0_0_40px_rgba(0,230,118,0.4)] transition-all">
            <Zap size={20} /> Commencer l'arbitrage maintenant
          </button>
          <div className="mt-4"><CountdownBanner /></div>
        </div>
      </section>

      {/* EMOTIONAL HOOK */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10 relative overflow-hidden" data-testid="emotional-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#00E676]/4 rounded-full blur-[130px] pointer-events-none" />
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <Sparkles className="text-[#FFCC00] mx-auto mb-6" size={40} />
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-extrabold tracking-tight leading-tight mb-6">
            L'outil que tu aurais aimé avoir<br className="hidden sm:block" /> <span className="text-[#00E676]">il y a quelques années...</span>
          </h2>
          <p className="text-lg text-white/50 max-w-2xl mx-auto mb-4">
            Combien de deals tu as ratés ? Combien d'heures perdues à chercher manuellement ?
          </p>
          <p className="text-xl text-white/70 font-bold mb-10">Aujourd'hui, tu as le choix. Profimy Margin est là.</p>
          <button onClick={handleCTA} className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-[#00E676] text-[#050505] font-bold text-lg sm:text-xl uppercase tracking-wider inline-flex items-center justify-center gap-3 hover:shadow-[0_0_50px_rgba(0,230,118,0.5)] transition-all">
            <Crown size={22} /> Je veux mon avantage
          </button>
          <div className="mt-4"><CountdownBanner /></div>
          <p className="text-sm text-white/30 mt-3">Sans engagement. Annulable en 1 clic. Zéro risque.</p>
        </div>
      </section>

      {/* FEATURES */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Tout ce dont tu as besoin</h2>
            <p className="text-white/50 text-lg">Un outil complet pour maximiser tes profits</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="group bg-[#0A0A0A] border border-white/10 p-6 hover:border-[#00E676]/40 hover:shadow-[0_0_20px_rgba(0,230,118,0.05)] transition-all">
                <div className="bg-[#00E676]/10 border border-[#00E676]/30 p-3 w-fit mb-4 group-hover:bg-[#00E676]/20 transition-colors"><Icon size={24} className="text-[#00E676]" /></div>
                <h3 className="font-bold text-lg mb-2 text-white">{title}</h3>
                <p className="text-sm text-white/50">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="border-t border-[#00E676]/10 px-4 sm:px-6 py-16 sm:py-20">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Comment ça marche ?</h2>
        </div>
        <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
          {[
            { step: "01", title: "Scanne", desc: "Recherche un produit ou parcours les opportunités détectées automatiquement." },
            { step: "02", title: "Compare", desc: "Vois instantanément les écarts de prix entre les plateformes d'achat et de vente." },
            { step: "03", title: "Profite", desc: "Achète au prix le plus bas, revends au prix le plus haut. Profit." },
          ].map(({ step, title, desc }) => (
            <div key={step} className="text-center">
              <div className="mx-auto w-16 h-16 border-2 border-[#00E676] text-[#00E676] flex items-center justify-center font-extrabold text-xl mb-4 shadow-[0_0_20px_rgba(0,230,118,0.2)]">{step}</div>
              <h3 className="font-bold text-xl mb-2">{title}</h3>
              <p className="text-sm text-white/50">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* PRICING - NO PRICE, just features + CTA */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10 relative" id="pricing">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-[#00E676]/3 rounded-full blur-[120px] pointer-events-none" />
        <div className="max-w-lg mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Prêt à passer à l'action ?</h2>
          <p className="text-white/50 text-lg mb-12">Accès complet à tous les outils Profimy Margin</p>
          <div className="bg-[#0A0A0A] border border-[#00E676]/40 p-10 relative shadow-[0_0_40px_rgba(0,230,118,0.1)]" data-testid="pricing-section">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#00E676] text-[#050505] px-6 py-1.5 font-bold text-sm uppercase tracking-wider">Profimy Pro</div>
            <div className="text-left space-y-3 mt-4 mb-8">
              {PLAN_FEATURES.map((f, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 bg-[#00E676]/20 border border-[#00E676]/50 flex items-center justify-center flex-shrink-0"><Check size={12} className="text-[#00E676]" /></div>
                  <span className="text-sm text-white/70">{f}</span>
                </div>
              ))}
            </div>
            <button onClick={handleCTA} className="w-full py-4 bg-[#00E676] text-[#050505] font-bold text-lg uppercase tracking-wider flex items-center justify-center gap-3 hover:shadow-[0_0_40px_rgba(0,230,118,0.5)] transition-all" data-testid="pricing-cta-btn">
              <Crown size={22} /> Commencer maintenant
            </button>
            <div className="mt-4"><CountdownBanner /></div>
            <p className="text-white/40 text-sm mt-3">Sans engagement — Annulable à tout moment</p>
          </div>
        </div>
      </section>

      {/* TRUSTPILOT */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 border-t border-[#00E676]/10" data-testid="trustpilot-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 mb-4">
              <Star className="text-[#00E676] fill-[#00E676]" size={24} />
              <span className="text-2xl font-extrabold">4.8</span><span className="text-white/40 text-lg">/5</span>
              <span className="text-white/60 font-bold ml-2">— Excellent — 140 avis</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-3">Ce que disent nos utilisateurs</h2>
            <a href="https://fr.trustpilot.com/review/josephtorregrossa.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-[#00E676] text-sm font-bold uppercase tracking-wider hover:underline" data-testid="trustpilot-link">
              Voir sur Trustpilot <ExternalLink size={14} />
            </a>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {TRUSTPILOT_REVIEWS.map((review, idx) => (
              <div key={idx} className="bg-[#0A0A0A] border border-white/10 p-5 hover:border-[#00E676]/30 transition-all" data-testid={`review-card-${idx}`}>
                <div className="flex items-center justify-between mb-3">
                  <StarRating rating={review.rating} />
                  {review.verified && <span className="text-[10px] text-[#00E676] border border-[#00E676]/40 px-2 py-0.5 uppercase tracking-wider font-bold">Vérifié</span>}
                </div>
                <p className="text-sm text-white/70 mb-4 leading-relaxed">"{review.text}"</p>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-sm text-white">{review.name}</span>
                  <span className="text-xs text-white/30">{review.date}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a href="https://fr.trustpilot.com/review/josephtorregrossa.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#00E676]/40 text-[#00E676] px-6 py-3 font-bold text-sm uppercase tracking-wider hover:bg-[#00E676]/10 transition-all">
              <Star size={16} className="fill-[#00E676]" /> Lire tous les avis sur Trustpilot
            </a>
          </div>
        </div>
      </section>

      {/* ACADEMY */}
      <section className="border-t border-[#00E676]/10 px-4 sm:px-6 py-12 sm:py-16 text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-extrabold mb-3">Formez-vous avec <span className="text-[#00E676] drop-shadow-[0_0_20px_rgba(0,230,118,0.3)]">Profimy Académie</span></h2>
          <p className="text-white/50 mb-8">Apprenez à vendre sur eBay, Amazon, Vinted et +50 autres plateformes avec nos formations complètes.</p>
          <a href="https://www.profimy-academie.com/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-[#00E676] text-[#00E676] px-8 py-3 font-bold text-lg uppercase tracking-wider hover:bg-[#00E676] hover:text-[#050505] transition-all hover:shadow-[0_0_30px_rgba(0,230,118,0.3)]" data-testid="academy-cta-btn">
            Découvrir l'Académie <ChevronRight size={20} />
          </a>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 bg-[#00E676] text-center" data-testid="final-cta-section">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#050505] mb-4">Prêt à faire de l'argent intelligemment ?</h2>
          <p className="text-[#050505]/70 text-lg mb-8">Rejoins les centaines de resellers qui utilisent déjà Profimy Margin pour trouver des deals rentables chaque jour.</p>
          <button onClick={handleCTA} className="w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-[#050505] text-[#00E676] font-bold text-base sm:text-xl uppercase tracking-wider inline-flex items-center justify-center gap-3 hover:shadow-[0_0_40px_rgba(0,0,0,0.5)] transition-all" data-testid="final-cta-btn">
            <Zap size={24} /> Démarrer maintenant
          </button>
          <div className="mt-4"><CountdownBanner /></div>
        </div>
      </section>

      {/* YOUTUBE TUTORIAL */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 bg-[#050505] border-t border-[#00E676]/10">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 uppercase tracking-tight">
            Tutoriel Profimy Margin
          </h2>
          <p className="text-white/50 text-sm mb-8 max-w-xl mx-auto">
            Decouvrez comment utiliser Profimy Margin pour trouver les meilleures opportunites d'arbitrage en quelques clics.
          </p>
          <div className="relative w-full" style={{ paddingBottom: "56.25%" }}>
            <iframe
              src="https://www.youtube.com/embed/aPyMctQJhbA"
              title="Tutoriel Profimy Margin"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full border-3 border-[#00E676]"
              style={{ border: "3px solid #00E676" }}
              data-testid="youtube-tutorial"
            />
          </div>
          <a
            href="https://youtu.be/aPyMctQJhbA"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 mt-6 px-6 py-3 bg-[#FF0000] text-white font-bold text-sm uppercase tracking-wider hover:bg-[#CC0000] transition-colors"
            data-testid="youtube-link"
          >
            <ExternalLink size={16} />
            Voir sur YouTube
          </a>
        </div>
      </section>

      {/* BLOG SECTION */}
      <section className="px-4 sm:px-6 py-16 sm:py-20 bg-[#050505] border-t border-[#00E676]/10">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3 uppercase tracking-tight">
            Blog Profimy
          </h2>
          <p className="text-white/50 text-sm mb-8 max-w-xl mx-auto">
            Strategies, guides et astuces pour maximiser vos marges sur les marketplaces.
          </p>
          <a
            href="/blog"
            className="inline-flex items-center gap-2 px-8 py-3 bg-[#00E676] text-[#050505] font-bold text-sm uppercase tracking-wider border-2 border-[#050505] hover:bg-[#00C853] transition-colors"
            data-testid="blog-link"
          >
            Lire le blog
            <ExternalLink size={16} />
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <SuitePromo current="profimy-app" />
      <footer className="border-t border-[#00E676]/10 px-4 sm:px-6 py-8 sm:py-10 bg-[#050505]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 mb-6">
            <div className="flex items-center gap-2">
              <span className="bg-[#00E676] text-[#050505] px-2 py-0.5 font-extrabold text-sm">PROFIMY</span>
              <span className="font-extrabold text-sm text-white">MARGIN</span>
            </div>
            <div className="flex items-center gap-4 sm:gap-6 text-xs sm:text-sm flex-wrap justify-center">
              <button onClick={() => navigate("/login")} className="text-white/40 hover:text-[#00E676] font-bold uppercase tracking-wider transition-colors">Connexion</button>
              <button onClick={() => navigate("/affiliation")} className="text-white/40 hover:text-[#00E676] font-bold uppercase tracking-wider transition-colors">Affiliation</button>
              <a href="https://www.profimy-academie.com/" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#00E676] font-bold uppercase tracking-wider transition-colors">Académie</a>
              <a href="https://fr.trustpilot.com/review/josephtorregrossa.com" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#00E676] font-bold uppercase tracking-wider transition-colors">Trustpilot</a>
              <a href="https://www.profimy-academie.com/mentions-legales" target="_blank" rel="noopener noreferrer" className="text-white/40 hover:text-[#00E676] font-bold uppercase tracking-wider transition-colors" data-testid="legal-link">Mentions Légales</a>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-white/30">profimy.app — Créé par Profimy™ — Fondateur : Joseph Torregrossa</p>
            <p className="text-xs text-white/20">Les résultats présentés sont des exemples et ne constituent pas une garantie de revenus.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
